import { CircleDashed, RotateCw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { isModuleAbsent, toUiError } from '../lib/errors.ts';
import { cn } from '../lib/cn.ts';
import { Button } from './Button.tsx';
import { EmptyState } from './EmptyState.tsx';

export interface ErrorStateProps {
  /** Anything thrown or an already-normalised { what, why, next }. */
  error: unknown;
  /** Shows a Retry button; spins while the returned promise runs. */
  onRetry?: () => void | Promise<void>;
  /** inline: a slim banner inside a panel. block: centred in a region. */
  variant?: 'block' | 'inline';
  /** Extra action beside Retry. */
  action?: ReactNode;
  className?: string;
}

/** An error that says what failed, why, and what to do next. Never a bare "Something went wrong". */
export function ErrorState({ error, onRetry, variant = 'block', action, className }: ErrorStateProps) {
  const e = toUiError(error);
  const [busy, setBusy] = useState(false);
  const retry = onRetry
    ? async () => {
        setBusy(true);
        try {
          await onRetry();
        } finally {
          setBusy(false);
        }
      }
    : undefined;

  // A feature this server does not run is not a failure: say so calmly, with what works instead.
  if (isModuleAbsent(error)) {
    if (variant === 'inline') {
      return (
        <div role="status" className={cn('flex items-start gap-3 rounded-card border border-border-subtle bg-surface py-2 pl-3 pr-2', className)}>
          <CircleDashed className="mt-0.5 size-4 shrink-0 text-fg-subtle" aria-hidden />
          <div className="min-w-0 flex-1 py-0.5 text-ui">
            <p className="font-medium text-fg">{e.what}</p>
            <p className="text-fg-muted">{e.why}</p>
          </div>
          {action}
        </div>
      );
    }
    return <EmptyState icon={<CircleDashed />} title={e.what} description={e.why} {...(action ? { action } : {})} {...(className ? { className } : {})} />;
  }

  if (variant === 'inline') {
    return (
      <div role="alert" className={cn('flex items-start gap-3 rounded-card border border-destructive-line bg-destructive-soft py-2 pl-3 pr-2', className)}>
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-status-danger" aria-hidden />
        <div className="min-w-0 flex-1 py-0.5 text-ui">
          <p className="font-medium text-fg">{e.what}</p>
          <p className="text-fg-muted">
            {e.why} {e.next}
          </p>
        </div>
        {retry && (
          <Button size="sm" variant="ghost" icon={<RotateCw />} loading={busy} onClick={retry}>
            Retry
          </Button>
        )}
        {action}
      </div>
    );
  }

  return (
    <div role="alert" className={cn('flex flex-col items-center justify-center gap-2 px-6 py-10 text-center', className)}>
      <div className="mb-1 flex size-10 items-center justify-center rounded-card border border-destructive-line bg-destructive-soft text-status-danger" aria-hidden>
        <TriangleAlert className="size-5" />
      </div>
      <p className="text-body font-medium text-fg">{e.what}</p>
      <p className="max-w-(--layout-width-interstitial) text-ui text-fg-muted">{e.why}</p>
      <p className="max-w-(--layout-width-interstitial) text-ui text-fg-muted">{e.next}</p>
      {(retry || action) && (
        <div className="mt-2 flex items-center gap-2">
          {retry && (
            <Button icon={<RotateCw />} loading={busy} onClick={retry}>
              Try again
            </Button>
          )}
          {action}
        </div>
      )}
    </div>
  );
}
